'use client';

import { useState, useEffect } from 'react';
import { getReports, runReport, ReportMetadata, RunReportParams } from '@/lib/api';
import ReportCard from '@/components/ReportCard';
import DateRangePicker from '@/components/DateRangePicker';
import ResultsTable from '@/components/ResultsTable';
import LoadingSpinner from '@/components/LoadingSpinner';
import ErrorMessage from '@/components/ErrorMessage';
import Navigation from '@/components/Navigation';
import { ReportCardSkeleton, TableSkeleton } from '@/components/SkeletonLoader';
import EmptyState from '@/components/EmptyState';
import ProtectedRoute from '@/components/ProtectedRoute';

function HomeContent() {
  const [reports, setReports] = useState<ReportMetadata[]>([]);
  const [selectedReport, setSelectedReport] = useState<ReportMetadata | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<unknown[]>([]);
  const [query, setQuery] = useState<string | null>(null);
  
  // Date range state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Additional filters
  const [customerId, setCustomerId] = useState('');
  const [agentId, setAgentId] = useState('');
  const [fileNumber, setFileNumber] = useState('');
  const [shipmentType, setShipmentType] = useState<'LCL' | 'FCL'>('LCL');
  const [maxMarginPct, setMaxMarginPct] = useState('');
  const [orderByProfit, setOrderByProfit] = useState(false);

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    try {
      setLoading(true);
      const reportsData = await getReports();
      setReports(reportsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectReport = (reportId: string) => {
    const report = reports.find((r) => r.id === reportId);
    if (report) {
      setSelectedReport(report);
      setResults([]);
      setQuery(null);
      setError(null);
      // Reset dates to current month
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      setStartDate(firstDay.toISOString().split('T')[0]);
      setEndDate(lastDay.toISOString().split('T')[0]);
    }
  };

  const handleRunReport = async () => {
    if (!selectedReport) return;

    try {
      setLoading(true);
      setError(null);

      const params: RunReportParams = {};
      
      if (selectedReport.supportsDateRange) {
        if (startDate) params.start = startDate;
        if (endDate) params.end = endDate;
      }
      
      if (selectedReport.supportsCustomerId && customerId) {
        params.customer_id = parseInt(customerId, 10);
      }
      
      if (selectedReport.supportsAgentId && agentId) {
        params.agent_id = parseInt(agentId, 10);
      }

      if (selectedReport.supportsFileNumber && fileNumber) {
        params.file_number = fileNumber.trim();
      }

      if (selectedReport.supportsShipmentType && shipmentType) {
        params.shipment_type = shipmentType;
      }

      if (selectedReport.supportsMaxMarginPct && maxMarginPct) {
        const pct = parseFloat(maxMarginPct);
        if (!isNaN(pct)) params.max_margin_pct = pct;
      }

      if (selectedReport.supportsOrderByProfit && orderByProfit) {
        params.order_by_profit = true;
      }

      const response = await runReport(selectedReport.id, params);
      setResults(response.data);
      setQuery(response.query);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to run report');
      setResults([]);
      setQuery(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Saved Reports</h1>

        {error && (
          <ErrorMessage 
            message={error} 
            onDismiss={() => setError(null)}
            retryAction={selectedReport ? {
              label: 'Retry Report',
              onClick: handleRunReport,
            } : undefined}
          />
        )}

        {!selectedReport ? (
          <div>
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[1, 2, 3, 4].map((i) => (
                  <ReportCardSkeleton key={i} />
                ))}
              </div>
            ) : reports.length === 0 ? (
              <EmptyState
                title="No Reports Available"
                description="Unable to load reports. Please check your connection and try again."
                action={{
                  label: "Retry",
                  onClick: loadReports,
                }}
              />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {reports.map((report) => (
                  <ReportCard
                    key={report.id}
                    report={report}
                    onSelect={handleSelectReport}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900">
                    {selectedReport.name}
                  </h2>
                  <p className="text-gray-600 mt-1">{selectedReport.description}</p>
                </div>
                <button
                  onClick={() => {
                    setSelectedReport(null);
                    setResults([]);
                    setQuery(null);
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900"
                >
                  ← Back to Reports
                </button>
              </div>

              {selectedReport.supportsDateRange && (
                <div className="mb-4">
                  <DateRangePicker
                    startDate={startDate}
                    endDate={endDate}
                    onStartDateChange={setStartDate}
                    onEndDateChange={setEndDate}
                  />
                </div>
              )}

              {selectedReport.supportsCustomerId && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer ID (optional)
                  </label>
                  <input
                    type="number"
                    value={customerId}
                    onChange={(e) => setCustomerId(e.target.value)}
                    placeholder="Enter customer ID"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              )}

              {selectedReport.supportsAgentId && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Agent ID (optional)
                  </label>
                  <input
                    type="number"
                    value={agentId}
                    onChange={(e) => setAgentId(e.target.value)}
                    placeholder="Enter agent ID"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              )}

              {selectedReport.supportsFileNumber && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    File / Shipment Number (required)
                  </label>
                  <input
                    type="text"
                    value={fileNumber}
                    onChange={(e) => setFileNumber(e.target.value)}
                    placeholder="e.g. GUY1225014"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              )}

              {selectedReport.supportsShipmentType && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Shipment Type
                  </label>
                  <select
                    value={shipmentType}
                    onChange={(e) => setShipmentType(e.target.value as 'LCL' | 'FCL')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="LCL">LCL (Less than Container Load)</option>
                    <option value="FCL">FCL (Full Container Load)</option>
                  </select>
                </div>
              )}

              {selectedReport.supportsOrderByProfit && (
                <div className="mb-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={orderByProfit}
                      onChange={(e) => setOrderByProfit(e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Order by highest profit (most profitable first)
                    </span>
                  </label>
                </div>
              )}

              {selectedReport.supportsMaxMarginPct && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max True Margin % (e.g. 20 for &quot;20% or less&quot;)
                  </label>
                  <input
                    type="number"
                    value={maxMarginPct}
                    onChange={(e) => setMaxMarginPct(e.target.value)}
                    placeholder="e.g. 20"
                    min={0}
                    max={100}
                    step={0.1}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              )}

              <button
                onClick={handleRunReport}
                disabled={loading || (selectedReport.supportsFileNumber && !fileNumber.trim())}
                className="w-full md:w-auto px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Running...' : 'Run Report'}
              </button>
            </div>

            {loading && (
              <div className="space-y-6">
                <TableSkeleton />
              </div>
            )}

            {query && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Query Used:</h3>
                <code className="text-xs text-gray-600 break-all">{query}</code>
              </div>
            )}

            {!loading && results.length === 0 && query && (
              <EmptyState
                title="No Results Found"
                description="The query executed successfully but returned no data for the selected parameters."
                icon={
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                }
              />
            )}

            {results.length > 0 && (
              <ResultsTable data={results} reportId={selectedReport.id} />
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default function Home() {
  return (
    <ProtectedRoute>
      <HomeContent />
    </ProtectedRoute>
  );
}
